/* eslint-disable max-len */
import { useRouter } from 'next/router';
import React, { FC, useState } from 'react';

import { Nullable } from '@common/type/CommonTypes';

import CourseDiscussionContentSkeleton from '../courseDiscussionContentSkeleton';

import CourseDiscussionItem, { CourseDiscussionItemType } from './courseDiscussionItem';
import ReplyDiscussionDialog from './replyDiscussionDialog';

import CourseDiscussionEmptyData from '@components/store/product/courseDiscussion/courseDiscussionEmptyData';
import CourseDiscussionNoAccess from '@components/store/product/courseDiscussion/courseDiscussionNoAccess';
import CourseDiscussionSearchNotFound from '@components/store/product/courseDiscussion/courseDiscussionSearchNotFound';
import { useCourseDiscussionList } from '@hooks/course/courseDiscussion/useCourseDiscussionList';
import { CourseDiscussionEntity } from '@redux/course/type/courseDetailType';

export type DiscussionReplyItems = {
  id: number;
  content: string;
  createdAt: Date;
  discussionId: number;
  user: {
    id: number;
    fullname: string;
    imageUrl?: string;
    isAuthor?: boolean;
  };
};

type DiscussionItems = {
  id: number;
  title: string;
  content?: string;
  createdAt: Date;
  discussionReplies: DiscussionReplyItems[];
  user: {
    id: number;
    fullname: string;
    imageUrl?: string;
    isAuthor?: boolean;
  };
};

type DiscussionDataProps = {
  discussionData: DiscussionItems[];
  isLoading: boolean;
  searchQuery: string;
  setSearchQuery: (searchQuery: string) => void;
  isDisplayCourseDiscussion: boolean;
};

const CourseDiscussionBox: FC<DiscussionDataProps> = ({
  discussionData,
  isLoading,
  searchQuery,
  setSearchQuery,
  isDisplayCourseDiscussion,
}) => {
  const router = useRouter();
  const idName = router.query.idName as string;
  const { isCourseEnrollmentLoading } = useCourseDiscussionList({ idName });

  const [selectedDiscussion, setSelectedDiscussion] = useState<Nullable<CourseDiscussionEntity>>(null);
  const [isOpenReplyDiscussionDialog, setIsOpenReplyDiscussionDialog] = useState<boolean>(false);

  const handleDiscussionViewInfo = (discussion: CourseDiscussionEntity) => {
    setSelectedDiscussion(discussion);
    setIsOpenReplyDiscussionDialog(true);
  };

  const hasData = discussionData.length !== 0;

  if (isCourseEnrollmentLoading) {
    return <CourseDiscussionContentSkeleton />;
  }
  if (!isDisplayCourseDiscussion) {
    return <CourseDiscussionNoAccess />;
  }
  if (isLoading && !hasData) {
    return <CourseDiscussionContentSkeleton />;
  }
  if (!isLoading && !hasData) {
    return searchQuery ? (
      <CourseDiscussionSearchNotFound setSearchQuery={setSearchQuery} />
    ) : (
      <CourseDiscussionEmptyData />
    );
  }

  return (
    <>
      {discussionData.map((item) => (
        <CourseDiscussionItem
          key={item.id}
          type={CourseDiscussionItemType.LIST_ITEM}
          imageUrl={item.user?.imageUrl || ''}
          fullName={item.user?.fullname}
          content={item.title}
          createdAt={item.createdAt}
          isAuthor={item.user?.isAuthor}
          replyCount={item.discussionReplies?.length}
          onClickViewInfo={() => handleDiscussionViewInfo(item)}
        />
      ))}

      <ReplyDiscussionDialog
        isOpen={isOpenReplyDiscussionDialog}
        setIsOpen={setIsOpenReplyDiscussionDialog}
        selectedDiscussion={selectedDiscussion}
        searchQuery={searchQuery}
      />
    </>
  );
};

export default CourseDiscussionBox;
